// Full-document render of the Oscar single-page checkout view: PageSkeleton
// (the React document shell) wrapping the custom checkout header, the
// fraud-critical CyberSource fingerprint + Google Maps loader scripts, and the
// `#checkout-app` SPA mount point.
import React from "react";

import { templates } from "@reactivated";

import { CheckoutApp } from "../apps/checkout/CheckoutApp";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CheckoutHeader } from "./CheckoutHeaderTemplate";
import { Template as MessageBanner } from "./MessageBannerTemplate";

export const Template = (
    props: templates.CheckoutDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            /* The legacy checkout {% block header %} replaced _header.html
               (which carried the standard tsicommon/_messages.html aside), so
               the default messages block is suppressed and the flash queue
               renders as MessageBanners instead: one per queued message, plus
               a trailing blank one whose MessageBanner surfaces the Redux
               payment_error set on a failed payment submission, then the
               checkout header. The footer block was not overridden, so the
               default SiteFooter renders. */
            messages={false}
            header={
                <>
                    {props.page_chrome.messages.map((message, i) => (
                        <MessageBanner
                            key={i}
                            tags={message.tags}
                            message={message.text}
                        />
                    ))}
                    <MessageBanner tags="" message="" />
                    <CheckoutHeader {...props.checkout_header} />
                </>
            }
        >
            {/* CyberSource device fingerprint (fraud-critical). The js loader
                is async so React 19 hoists it to <head> as a resource instead
                of leaving a positional <script> in the React-owned body that
                trips a full-document hydration mismatch; the device profiling
                still completes long before the order is placed. */}
            <p
                style={{
                    background: `url(${props.fingerprint_img_1_url})`,
                }}
            />
            <img src={props.fingerprint_img_2_url} alt="Redirect Icon" />
            <object
                type="application/x-shockwave-flash"
                data={props.fingerprint_flash_url}
                width={1}
                height={1}
                id="thm_fp"
            >
                <param name="movie" value={props.fingerprint_flash_url} />
                <div />
            </object>
            <script
                src={props.fingerprint_js_url}
                type="text/javascript"
                async
            />
            {/* Google Maps Places powers the address-autocomplete inputs. async
                so React 19 hoists it to <head> (a resource matched by src)
                rather than a positional body <script> that trips a
                full-document hydration mismatch; it still loads well before the
                checkout's address step renders. */}
            <script
                src={`https://maps.googleapis.com/maps/api/js?v=3&key=${props.google_maps_api_key}&libraries=places`}
                type="text/javascript"
                async
            />
            {/* The checkout app lives in the document's React tree (mounted
                client-side by CheckoutApp; the dependency tree is not
                SSR-safe). */}
            <div className="l-capped-width">
                <div id="checkout-app" className="checkout-app-container">
                    <CheckoutApp />
                </div>
            </div>
        </PageSkeleton>
    );
};
